import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import {
  calculateTrend,
  calculatePercentageChange,
  getTrendStatus,
  getLastNDaysRecords,
  extractMetricValues,
} from "./utils/healthCalculations";

const HealthOverview = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [records, setRecords] = useState([]);
  const [metrics, setMetrics] = useState({});

  useEffect(() => {
    if (user?.dailyRecords?.records) {
      const last30Days = getLastNDaysRecords(user.dailyRecords.records, 30);
      setRecords(last30Days);
      calculateMetrics(last30Days);
    }
  }, [user]);

  const calculateMetrics = (data) => {
    if (data.length === 0) return;

    const metricsData = {
      weight: {
        label: "Weight",
        unit: "kg",
        values: extractMetricValues(data, "weight"),
        color: "#3b82f6",
        isIncreaseBetter: false,
      },
      fat: {
        label: "Body Fat",
        unit: "%",
        values: extractMetricValues(data, "fat"),
        color: "#ef4444",
        isIncreaseBetter: false,
      },
      muscles: {
        label: "Muscle Mass",
        unit: "%",
        values: extractMetricValues(data, "muscles"),
        color: "#10b981",
        isIncreaseBetter: true,
      },
      bmi: {
        label: "BMI",
        unit: "",
        values: extractMetricValues(data, "bmi"),
        color: "#f59e0b",
        isIncreaseBetter: false,
      },
    };

    // Calculate trends and changes for each metric
    Object.keys(metricsData).forEach((key) => {
      const values = metricsData[key].values;
      if (values.length > 0) {
        const firstValue = values[0];
        const lastValue = values[values.length - 1];
        const trend = calculateTrend(values);
        const change = calculatePercentageChange(firstValue, lastValue);
        const status = getTrendStatus(
          key,
          trend,
          metricsData[key].isIncreaseBetter
        );

        metricsData[key] = {
          ...metricsData[key],
          current: lastValue,
          first: firstValue,
          change: parseFloat(change),
          absoluteChange: (lastValue - firstValue).toFixed(1),
          trend,
          status,
        };
      }
    });

    setMetrics(metricsData);
  };

  const MetricCard = ({ metric, data }) => {
    if (!data || !data.values || data.values.length === 0) return null;

    const chartData = data.values.map((value, index) => ({ value }));
    const isPositive = data.change > 0;
    const isImproving = data.status.color === "green";

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-600">{data.label}</h3>
          <span
            className={`text-xs px-2 py-1 rounded-full ${
              data.status.color === "green"
                ? "bg-green-100 text-green-700"
                : data.status.color === "red"
                ? "bg-red-100 text-red-700"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            {data.status.status}
          </span>
        </div>

        {/* Current Value */}
        <div className="mb-3">
          <div className="text-3xl font-bold text-gray-900">
            {data.current?.toFixed(1)}
            <span className="text-lg text-gray-500 ml-1">{data.unit}</span>
          </div>
        </div>

        {/* Sparkline Chart */}
        <div className="h-12 mb-3">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={data.color}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Change Indicator */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1">
            {data.trend === "up" ? (
              <TrendingUp className="w-4 h-4 text-blue-600" />
            ) : data.trend === "down" ? (
              <TrendingDown className="w-4 h-4 text-blue-600" />
            ) : (
              <Minus className="w-4 h-4 text-gray-400" />
            )}
            <span
              className={`font-medium ${
                isImproving ? "text-green-600" : "text-red-600"
              }`}
            >
              {isPositive ? "+" : ""}
              {data.absoluteChange} {data.unit}
            </span>
          </div>
          <span className="text-gray-500">
            {isPositive ? "+" : ""}
            {data.change}% in 30 days
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="w-full max-w-4xl mx-auto bg-white shadow-sm">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/profile")}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Body Overview</h1>
              <p className="text-sm text-gray-500">Last 30 days</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {records.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                No health records found for the last 30 days
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Start tracking your daily measurements to see trends
              </p>
            </div>
          ) : (
            <>
              {/* Summary Message */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 mb-6 border border-blue-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  📊 Am I moving in the right direction?
                </h2>
                <p className="text-sm text-gray-700">
                  Based on your {records.length}-day tracking, here's your
                  progress across key health metrics.
                </p>
              </div>

              {/* Metric Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.keys(metrics).map((key) => (
                  <MetricCard key={key} metric={key} data={metrics[key]} />
                ))}
              </div>

              {/* Additional Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  💡 Understanding Your Trends
                </h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>
                    • <span className="text-green-600 font-medium">Green</span>{" "}
                    = Improving trend
                  </li>
                  <li>
                    • <span className="text-red-600 font-medium">Red</span> =
                    Needs attention
                  </li>
                  <li>
                    • <span className="text-gray-600 font-medium">Gray</span> =
                    Stable (no significant change)
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HealthOverview;
